/**
 * FAQs for /non-surgical-face-lift and /facial-balancing.
 *
 * One source for both the visible FAQ on the page and the FAQPage JSON-LD (the schema must describe
 * content visitors can see). Every figure comes from a source that already prints it live: the XERF
 * page (intro $1,250 / $3,200, 1-2 sessions about 4 weeks apart, sometimes up to 3) and the facial
 * balancing pricing table owner-approved 2026-10-08 (client/src/pages/lp/facialBalancing/pricing.ts).
 * server/faceLiftPages.test.ts fails if these numbers drift from that table.
 */
export type FaceLiftFaq = { q: string; a: string };

export const NON_SURGICAL_FACE_LIFT_FAQS: FaceLiftFaq[] = [
  {
    q: "What is a non-surgical face lift?",
    a: "A non-surgical face lift is a plan that combines treatments chosen for what is changing in your face without surgery: loose skin, lost volume, weakened deep support, or lines. At Lakewood Med Spa, Dr. Rajan Kohli, MD plans it around your anatomy and may combine options such as XERF, HIFU, PDO threads, fillers, and biostimulators.",
  },
  {
    q: "Can a non-surgical face lift replace a surgical facelift?",
    a: "No. Non-surgical options can improve mild-to-moderate looseness and volume loss, but they do not remove excess skin or reposition tissue the way surgery does. If your goals need surgery, Dr. Kohli will tell you honestly at your consultation and can refer you to a surgeon.",
  },
  {
    q: "Is a non-surgical face lift the same as a liquid facelift?",
    a: "They overlap. A liquid facelift usually means filler, sometimes with a collagen biostimulator, to restore structure. A non-surgical face lift is broader and can also include skin tightening such as XERF or HIFU, and PDO threads. Dr. Kohli explains which approach fits you at a complimentary consultation.",
  },
  {
    q: "Which treatments can be part of a non-surgical face lift?",
    a: "Depending on your anatomy, a plan can include XERF radiofrequency skin tightening, HIFU focused ultrasound, PDO threads, hyaluronic acid filler, collagen biostimulators such as Sculptra or Radiesse, and Botox, Dysport, or Xeomin. Most people do not need all of them, and the order is decided for you.",
  },
  {
    q: "How much does a non-surgical face lift cost in Dallas?",
    a: "The cost depends on the treatments in your plan. Introductory XERF pricing is $1,250 for one session or $3,200 for three. Filler is priced per syringe and your plan is priced before treatment. Other options are quoted at your consultation, which is complimentary.",
  },
  {
    q: "How many treatments will I need?",
    a: "It depends on your plan. XERF is often planned as one to two sessions about four weeks apart, sometimes up to three. Other options follow different schedules, so your timeline is individualized and reviewed with you at your consultation before you decide on anything.",
  },
  {
    q: "Where can I get a non-surgical face lift in Dallas?",
    a: "Lakewood Med Spa is at 8210 Walnut Hill Ln, Professional 1, #516, Dallas, TX 75231, on the Texas Health Presbyterian Hospital campus near US-75. Dr. Rajan Kohli, MD evaluates patients from Dallas, Lakewood, Preston Hollow, Highland Park, University Park, Plano, Frisco, and Richardson.",
  },
];

export const FACIAL_BALANCING_FAQS: FaceLiftFaq[] = [
  {
    q: "What is facial balancing?",
    a: "Facial balancing is a plan that treats several areas together, such as the lips, cheeks, chin, jawline, temples, and under-eyes, so they look proportionate to one another instead of being treated in isolation. At Lakewood Med Spa, Dr. Rajan Kohli, MD maps the plan and performs the treatment.",
  },
  {
    q: "How much does facial balancing cost in Dallas?",
    a: "Hyaluronic acid filler is priced per syringe: $675 for lips, $850 for cheeks, chin, jawline, or temples, and $950 for under-eye or non-surgical nose. In a multi-syringe plan the first syringe is charged at its area price and each additional syringe is $700, never more than pricing each separately. Your plan is priced before treatment.",
  },
  {
    q: "How many syringes will I need?",
    a: "It depends on the areas you want treated and your anatomy. Some people start with one or two syringes and others plan more. Dr. Kohli maps the areas and the order at your consultation, so you see the plan and its price before you decide on anything.",
  },
  {
    q: "Is facial balancing the same as a liquid facelift?",
    a: "They are related. Facial balancing focuses on proportion across the lips, cheeks, chin, and jawline. A liquid facelift usually adds structure with filler plus a collagen biostimulator. Both are non-surgical and planned by Dr. Kohli, and our non-surgical face lift page explains the wider set of options.",
  },
  {
    q: "Is the facial balancing consultation free?",
    a: "Yes. The consultation is complimentary. Dr. Kohli reviews your goals and anatomy, maps the areas, and prices the plan before any treatment, so there is nothing to decide on the spot and you can compare the plan against what you were expecting.",
  },
  {
    q: "Where do you offer facial balancing in Dallas?",
    a: "Lakewood Med Spa is at 8210 Walnut Hill Ln, Professional 1, #516, Dallas, TX 75231, on the Texas Health Presbyterian Hospital campus near US-75. Patients visit from Dallas, Lakewood, Preston Hollow, Highland Park, University Park, Plano, Frisco, and Richardson.",
  },
];
